(function () {
  if (customElements.get('p-sale-compare-selector')) return;

  class PSaleCompareSelector extends HTMLElement {
    constructor() {
      super();
      this.desktopMedia = window.matchMedia('(min-width: 851px)');
      this.selectedIndices = [];

      this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
      this.handleSelectChange = this.handleSelectChange.bind(this);
    }

    connectedCallback() {
      this.collectElements();
      if (this.productCards.length === 0) return;

      this.selectElements.forEach((select) => {
        select.addEventListener('change', this.handleSelectChange);
      });
      this.initializeSelections();
      this.applyState();

      if (typeof this.desktopMedia.addEventListener === 'function') {
        this.desktopMedia.addEventListener('change', this.handleBreakpointChange);
      } else if (typeof this.desktopMedia.addListener === 'function') {
        this.desktopMedia.addListener(this.handleBreakpointChange);
      }
    }

    disconnectedCallback() {
      this.selectElements.forEach((select) => {
        select.removeEventListener('change', this.handleSelectChange);
      });
      if (typeof this.desktopMedia.removeEventListener === 'function') {
        this.desktopMedia.removeEventListener('change', this.handleBreakpointChange);
      } else if (typeof this.desktopMedia.removeListener === 'function') {
        this.desktopMedia.removeListener(this.handleBreakpointChange);
      }
    }

    collectElements() {
      this.productsContainer = this.querySelector('[data-products]');
      this.parametersContainer = this.querySelector('[data-parameters]');
      this.productCards = Array.from(this.querySelectorAll('[data-product-card]'));
      this.parameterColumns = Array.from(this.querySelectorAll('[data-parameters-column]'));
      this.selectElements = Array.from(this.querySelectorAll('[data-product-selector]'));
      this.pcProducts = this.parseCount(this.dataset.pcProducts, this.productCards.length || 3);
      this.mbProducts = this.parseCount(this.dataset.mbProducts, 1);
      this.productIndices = this.productCards
        .map((card) => this.getProductIndex(card))
        .filter((index) => Number.isInteger(index));
      this.productCardsByIndex = new Map(
        this.productCards.map((card) => [this.getProductIndex(card), card])
      );
      this.parameterColumnsByIndex = new Map(
        this.parameterColumns.map((column) => [this.getProductIndex(column), column])
      );
    }

    parseCount(value, fallback) {
      const count = Number.parseInt(value, 10);
      return Number.isNaN(count) ? fallback : count;
    }

    getProductIndex(element) {
      return Number.parseInt(element.dataset.productIndex, 10);
    }

    initializeSelections() {
      this.selectedIndices = [...this.productIndices];
      this.syncSelectedIndices();
    }

    handleBreakpointChange() {
      this.syncSelectedIndices();
      this.applyState();
    }

    getActiveCount() {
      const configuredCount = this.desktopMedia.matches ? this.pcProducts : this.mbProducts;
      return Math.min(configuredCount, this.selectedIndices.length);
    }

    syncSelectedIndices() {
      const validSet = new Set(this.productIndices);
      this.selectedIndices = this.selectedIndices.filter((index) => validSet.has(index));
      this.productIndices.forEach((index) => {
        if (!this.selectedIndices.includes(index)) {
          this.selectedIndices.push(index);
        }
      });
    }

    applyState() {
      const activeCount = this.getActiveCount();
      const activeIndices = this.selectedIndices.slice(0, activeCount);
      const hiddenIndices = this.selectedIndices.slice(activeCount);

      this.dataset.device = this.desktopMedia.matches ? 'pc' : 'mb';
      this.dataset.activeCount = String(activeCount);
      this.syncDomOrder();

      this.productIndices.forEach((index) => {
        const displayIndex = activeIndices.indexOf(index);
        const isActive = displayIndex !== -1;

        this.updateElementState(this.productCardsByIndex.get(index), isActive, displayIndex);
        this.updateElementState(this.parameterColumnsByIndex.get(index), isActive, displayIndex);
        this.updateSelectorState(index, isActive, displayIndex, hiddenIndices);
      });
    }

    syncDomOrder() {
      if (this.productsContainer) {
        this.selectedIndices.forEach((index) => {
          const card = this.productCardsByIndex.get(index);
          if (card) {
            this.productsContainer.appendChild(card);
          }
        });
      }

      if (this.parametersContainer) {
        this.selectedIndices.forEach((index) => {
          const column = this.parameterColumnsByIndex.get(index);
          if (column) {
            this.parametersContainer.appendChild(column);
          }
        });
      }
    }

    updateElementState(element, isActive, displayIndex) {
      if (!element) return;

      element.classList.toggle('is-active', isActive);
      element.classList.toggle('is-hidden', !isActive);
      element.dataset.active = isActive ? 'true' : 'false';
      element.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      element.style.display = isActive ? 'block' : 'none';

      if (isActive) {
        element.dataset.displayIndex = String(displayIndex);
      } else {
        delete element.dataset.displayIndex;
      }
    }

    updateSelectorState(productIndex, isActive, displayIndex, hiddenIndices) {
      const card = this.productCardsByIndex.get(productIndex);
      if (!card) return;

      const select = card.querySelector('[data-product-selector]');
      if (!select) return;

      select.disabled = !isActive;

      if (isActive) {
        select.dataset.displaySlot = String(displayIndex);
      } else {
        delete select.dataset.displaySlot;
      }

      const allowedIndices = new Set([productIndex, ...hiddenIndices]);
      Array.from(select.options).forEach((option) => {
        const optionIndex = Number.parseInt(option.value, 10);
        const shouldShow = allowedIndices.has(optionIndex);

        option.hidden = !shouldShow;
        option.disabled = !shouldShow;
        option.style.display = shouldShow ? '' : 'none';

        if (optionIndex === productIndex) {
          option.selected = true;
        }
      });
      select.value = String(productIndex);
    }

    handleSelectChange(event) {
      const select = event.currentTarget;
      const currentIndex = Number.parseInt(select.dataset.productIndex, 10);
      const nextIndex = Number.parseInt(select.value, 10);
      const activeCount = this.getActiveCount();
      const currentPosition = this.selectedIndices.indexOf(currentIndex);
      const nextPosition = this.selectedIndices.indexOf(nextIndex);

      if (
        !Number.isInteger(currentPosition)
        || currentPosition < 0
        || currentPosition >= activeCount
        || !Number.isInteger(nextPosition)
        || nextPosition < 0
      ) {
        this.applyState();
        return;
      }

      if (currentIndex === nextIndex) return;

      [this.selectedIndices[currentPosition], this.selectedIndices[nextPosition]] = [
        this.selectedIndices[nextPosition],
        this.selectedIndices[currentPosition],
      ];
      this.applyState();
    }
  }

  customElements.define('p-sale-compare-selector', PSaleCompareSelector);
})();
